import { TARGET_CHANNEX } from '@core/config/rate-grid-features.config';
import type { BookingBucket, RateGridBookingSummary } from '@interfaces/rate-grid.interface';
import { dateOf, daysInMonth } from '@utils/rate-grid-date.util';

import {
  CURRENCY,
  type MockDay,
  TARGET_WEB,
  embeddedBookingOf,
  occupancyOf,
  targetAt,
  withRestrictions,
} from './rate-grid-mock.model';

/**
 * The months that exist only to be looked at. Each one gathers a family of cases the calendar draws
 * so the variants can be compared side by side instead of being conjured one at a time by editing:
 * the month after today holds every shape a stay can take, the next every source a price can have,
 * then the restrictions, then the publication states, then everything Channex arrives as.
 */
export type ShowcaseTheme = 'bookings' | 'prices' | 'restrictions' | 'sync' | 'channex';

export const SHOWCASE_THEMES: readonly ShowcaseTheme[] = [
  'bookings',
  'prices',
  'restrictions',
  'sync',
  'channex',
];

/** A destination no build knows, which v6 §9 has ignored visually rather than drawn half-named. */
const TARGET_UNKNOWN = 'FUTURE_CHANNEL';

interface ShowcaseContext {
  month: string;
  season: { id: string; name: string; colour: string };
  seasonPrice: number;
  actor: string;
}

/** Every stay the showcase lays down, kept so `booking-summary` can answer for any of its nights. */
export const SHOWCASE_BOOKINGS = new Map<string, RateGridBookingSummary>();

function summaryOf(
  id: string,
  guestDisplayName: string,
  channel: string,
  bucket: BookingBucket,
  month: string,
  from: number,
  nights: number,
  price: number
): RateGridBookingSummary {
  return {
    rentalId: '',
    bookingId: id,
    reference: `RSD-SHOW-${id}`,
    guestDisplayName,
    checkIn: dateOf(month, from),
    checkOut: dateOf(month, Math.min(from + nights, daysInMonth(month))),
    bucket,
    substate: null,
    bookingStatus:
      bucket === 'CONFIRMED'
        ? 'CHECKIN_SCHEDULED'
        : bucket === 'ACTIVE'
          ? 'CHECKOUT_PENDING'
          : bucket === 'FINISHED'
            ? 'FINISHED'
            : 'ACCEPTANCE_PENDING',
    channel,
    total: { amount: price * nights, currency: CURRENCY },
    navigationPath: null,
  };
}

/**
 * Lays a stay over its nights. Nothing is written to the morning after: a checkout day is an
 * ordinary, sellable night, and the pill reaching into it is drawn from the night before.
 */
function stay(
  days: Map<number, MockDay>,
  from: number,
  nights: number,
  summary: RateGridBookingSummary,
  bucket: BookingBucket
): void {
  SHOWCASE_BOOKINGS.set(summary.bookingId, summary);
  const occupancy = occupancyOf(
    summary.bookingId,
    summary.reference,
    summary.guestDisplayName,
    bucket,
    summary.channel,
    embeddedBookingOf(summary)
  );
  for (let day = from; day < from + nights; day += 1) {
    const entry = days.get(day);
    if (entry) {
      days.set(day, { ...entry, occupancy: { ...occupancy } });
    }
  }
}

function patch(days: Map<number, MockDay>, day: number, change: Partial<MockDay>): void {
  const entry = days.get(day);
  if (entry) {
    days.set(day, { ...entry, ...change });
  }
}

function rules(
  days: Map<number, MockDay>,
  day: number,
  overrides: Parameters<typeof withRestrictions>[1]
): void {
  const entry = days.get(day);
  if (entry) {
    days.set(day, withRestrictions(entry, overrides));
  }
}

/** Every shape a stay can take, including the ones only a row or month boundary produces. */
function bookings(days: Map<number, MockDay>, { month }: ShowcaseContext): void {
  const total = daysInMonth(month);
  const at = (value: number) => Math.min(value, total - 1);

  stay(
    days,
    at(2),
    1,
    summaryOf('one', 'Ana López', 'Web directa', 'CONFIRMED', month, at(2), 1, 300),
    'CONFIRMED'
  );
  stay(
    days,
    at(5),
    4,
    summaryOf('long', 'Carlos Pérez', 'Booking.com', 'CONFIRMED', month, at(5), 4, 300),
    'CONFIRMED'
  );
  // Back to back: the second stay checks in the morning the first checks out.
  stay(
    days,
    at(12),
    2,
    summaryOf('b2b-a', 'Ivan Petrov', 'Airbnb', 'FINISHED', month, at(12), 2, 300),
    'FINISHED'
  );
  stay(
    days,
    at(14),
    3,
    summaryOf('b2b-b', 'Nora Haddad', 'Web directa', 'CONFIRMED', month, at(14), 3, 300),
    'CONFIRMED'
  );
  // Crosses a row boundary, so it is drawn in two halves.
  stay(
    days,
    at(19),
    5,
    summaryOf('split', 'Sofia Ricci', 'Booking.com', 'ACTIVE', month, at(19), 5, 300),
    'ACTIVE'
  );
  // Runs past the end of the month, so this month never shows its checkout.
  stay(
    days,
    total - 1,
    4,
    summaryOf('over', 'Tom Becker', 'Airbnb', 'PENDING', month, total - 1, 4, 300),
    'PENDING'
  );
}

/** One night for each source a price can have, plus an unpriced night. */
function prices(days: Map<number, MockDay>, ctx: ShowcaseContext): void {
  patch(days, 3, { price: { amount: 300, currency: CURRENCY, source: 'BASE' } });
  patch(days, 6, { price: { amount: ctx.seasonPrice, currency: CURRENCY, source: 'SEASON' } });
  patch(days, 9, {
    price: { amount: 420, currency: CURRENCY, source: 'MANUAL' },
    lastChange: { type: 'PRICE', at: `${dateOf(ctx.month, 9)}T10:00:00Z`, actor: ctx.actor },
  });
  patch(days, 12, {
    price: { amount: 265, currency: CURRENCY, source: 'CHANNEL_SYNC' },
    lastChange: { type: 'PRICE', at: `${dateOf(ctx.month, 12)}T03:00:00Z`, actor: 'channex-sync' },
  });
  patch(days, 15, { price: { amount: 380, currency: CURRENCY, source: 'MANUAL' } });
  // The one state the grid draws from an absent price rather than from a flag.
  patch(days, 18, { price: { amount: null, currency: CURRENCY, source: 'BASE' } });
}

/** Stay rules, arrival and departure closures, and closures, one per night. */
function restrictions(days: Map<number, MockDay>, _ctx: ShowcaseContext): void {
  rules(days, 3, { minStay: 5 });
  rules(days, 5, { maxStay: 3 });
  rules(days, 7, { minStay: 2, maxStay: 4 });
  rules(days, 10, { cta: true });
  rules(days, 12, { ctd: true });
  rules(days, 14, { cta: true, ctd: true });
  patch(days, 22, { manualClosure: { closed: true, reason: 'Mantenimiento' } });
  patch(days, 23, { manualClosure: { closed: true, reason: 'Uso propio' } });
  patch(days, 26, {
    restrictions: {
      overrides: { minStay: null, maxStay: null, cta: null, ctd: null },
      effective: { minStay: 2, maxStay: 14, cta: false, ctd: false },
    },
  });
}

/**
 * Each publication state a night can be in. Day 8 is the one the manual retry exists for: failed on
 * the web after its automatic attempts, and authorised by the server to be pushed again.
 */
function sync(days: Map<number, MockDay>, ctx: ShowcaseContext): void {
  const at = `${dateOf(ctx.month, 1)}T00:00:00Z`;
  const manual = (amount: number) => ({
    price: { amount, currency: CURRENCY, source: 'MANUAL' as const },
  });

  patch(days, 4, {
    ...manual(315),
    targets: [targetAt(TARGET_WEB, 'PENDING', at, 'op-showcase-4')],
  });
  patch(days, 8, {
    ...manual(330),
    targets: [targetAt(TARGET_WEB, 'FAILED', at, 'op-showcase-8', true)],
  });
  patch(days, 12, {
    ...manual(355),
    targets: [targetAt(TARGET_WEB, 'FAILED', at, 'op-showcase-12', false)],
  });
  patch(days, 16, {
    ...manual(290),
    targets: [targetAt(TARGET_WEB, 'PUBLISHED', at)],
  });
}

/**
 * The month the Channex integration is read through: stays that arrived from Booking.com under all
 * three names the channel travels as and from Airbnb under its name and its code, nights published
 * to Channex alongside the web, a Channex publication left pending and one that failed, and a
 * destination this build has never heard of.
 *
 * It is the one place that models the contract Channex is *asked* for rather than the one the
 * backend serves today: the real read carries no `CHANNEX` capability, never writes a `CHANNEX`
 * target row, and puts no channel on a day's occupancy.
 */
function channex(days: Map<number, MockDay>, ctx: ShowcaseContext): void {
  const at = `${dateOf(ctx.month, 1)}T00:00:00Z`;
  const total = daysInMonth(ctx.month);
  const on = (value: number) => Math.min(value, total - 1);

  stay(
    days,
    on(3),
    3,
    summaryOf('bdc', 'Emma Schmidt', 'BDC', 'CONFIRMED', ctx.month, on(3), 3, 300),
    'CONFIRMED'
  );
  stay(
    days,
    on(10),
    2,
    summaryOf('bdc-named', 'Liam Novak', 'Booking.com', 'ACTIVE', ctx.month, on(10), 2, 300),
    'ACTIVE'
  );
  stay(
    days,
    on(16),
    2,
    summaryOf('bdc-code', 'Yuki Tanaka', 'CHANNEX/BDC', 'CONFIRMED', ctx.month, on(16), 2, 300),
    'CONFIRMED'
  );
  stay(
    days,
    on(13),
    2,
    summaryOf('abb-named', 'Lucía Romero', 'Airbnb', 'CONFIRMED', ctx.month, on(13), 2, 300),
    'CONFIRMED'
  );
  stay(
    days,
    on(24),
    2,
    summaryOf('abb-code', 'Owen Hughes', 'ABB', 'CONFIRMED', ctx.month, on(24), 2, 300),
    'CONFIRMED'
  );

  patch(days, 6, {
    price: { amount: 340, currency: CURRENCY, source: 'MANUAL' },
    targets: [targetAt(TARGET_WEB, 'PUBLISHED', at), targetAt(TARGET_CHANNEX, 'PUBLISHED', at)],
  });
  // Left pending for good: a Channex publication the grid must neither wait on nor keep re-reading.
  patch(days, 20, {
    price: { amount: 360, currency: CURRENCY, source: 'MANUAL' },
    targets: [
      targetAt(TARGET_WEB, 'PUBLISHED', at),
      targetAt(TARGET_CHANNEX, 'PENDING', null, 'op-channex-20'),
    ],
  });
  // Failed and marked retryable by the backend, which Front must still refuse to offer a retry for.
  patch(days, 23, {
    price: { amount: 375, currency: CURRENCY, source: 'MANUAL' },
    targets: [
      targetAt(TARGET_WEB, 'PUBLISHED', at),
      targetAt(TARGET_CHANNEX, 'FAILED', at, 'op-channex-23', true),
    ],
  });
  patch(days, 26, {
    targets: [
      targetAt(TARGET_WEB, 'PUBLISHED', at),
      targetAt(TARGET_UNKNOWN, 'FAILED', at, 'op-unknown-26', true),
    ],
  });
}

const BUILDERS: Record<ShowcaseTheme, (days: Map<number, MockDay>, ctx: ShowcaseContext) => void> =
  {
    bookings,
    prices,
    restrictions,
    sync,
    channex,
  };

/** Applies a theme's cases over a plain month, returning the nights in date order. */
export function buildShowcaseMonth(
  theme: ShowcaseTheme,
  plain: MockDay[],
  ctx: ShowcaseContext
): MockDay[] {
  const days = new Map<number, MockDay>(
    plain.map(day => [Number(day.date.slice(-2)), day] as const)
  );
  BUILDERS[theme](days, ctx);
  return [...days.values()].sort((left, right) => left.date.localeCompare(right.date));
}
